// @flow
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

import memoize from "lodash/memoize";

import { getLocales, getLocale } from "../redux/selectors/i18n";
import { updateLocale } from "../redux/_i18n/I18nActions";

import type Locales from "../i18n/Locales";

const getMessage = createSelector(
  [getLocales, getLocale],
  (locales, localeCode) =>
    memoize(
      (id: string, defaultMessage?: string, placeholders?: Object) =>
        locales
          .getLocale(localeCode)
          .getMessage(id, defaultMessage, placeholders),
      (...args) => JSON.stringify(args),
    ),
);

/**
 */
const useMessage = (
  id: string,
  defaultMessage?: string,
  placeholders?: Object | null,
): any => useSelector(getMessage)(id, defaultMessage, placeholders);

/**
 */
const useTranslate = (): ((...any) => any) => useSelector(getMessage);

/**
 */
const useLocale = (): string => useSelector(getLocale);

/**
 */
const useLocales = (): Locales => useSelector(getLocales);

const getLocaleCodes = createSelector(
  (state) => state.i18n.locales,
  (locales) => locales.map((locale) => locale.code),
);

/**
 * Retrieve all available locale codes
 */
const useLocaleCodes = (): Array<string> => useSelector(getLocaleCodes);

/**
 * A hook that returns a memoized function to update the application locale.
 *
 * @returns {(locale: string) => void} A memoized function to update the locale.
 */
const useUpdateLocale = (): ((locale: string) => void) => {
  const dispatch = useDispatch();

  return useCallback(
    (locale: string) => {
      dispatch(updateLocale(locale));
    },
    [dispatch],
  );
};

export {
  useTranslate,
  useMessage,
  useLocale,
  useLocales,
  useLocaleCodes,
  useUpdateLocale,
};
